// weeklyTasks.ts — week-level aggregation of a timesheet's piece / misc claims.
//
// Tenants with `combineTasksInWeek` (npmech) let employees claim pieces for the
// WEEK rather than for a day: the same task shows up on several days and the
// per-day split is meaningless. Both review surfaces roll those rows up the same
// way — v1's TimesheetPage "Weekly Tasks" block and the v2 console's Weekly Tasks
// Summary — so the aggregation lives here rather than inside either page.
//
// Extracted verbatim (behaviour-preserving) from TimesheetPage.tsx's local
// `aggregateWeeklyProjectTasks`, with the `any`s replaced by the real payload
// shapes. Two properties of the original are deliberately kept:
//   • `task` / `accountCode` are captured from the FIRST occurrence in the week —
//     `originalAmount` / `remainingAmount` are task-level totals, not per-day
//     values, so summing them would be wrong.
//   • project identity falls back to `altProjectID` when `projectID` is absent,
//     matching the original key expression.

import { Day, EmployeeTask, MiscEmployeeTask, Project, Task, Account } from '../interfaces/EmployeeInterface';

/** One task claimed across the week, with its per-week claimed total. */
export interface WeeklyEmployeeTask {
  task: Task;
  amountClaimed: number;
  intermediateName: string;
}

/** One misc claim rolled up across the week. */
export interface WeeklyMiscEmployeeTask {
  miscDescription: string;
  accountCode: Account;
  amountClaimed: number;
  intermediateName: string;
}

/** A project's whole week of piece + misc claims. Mirrors ProjectEmployeeTasks so
 *  the same renderers accept either a day's rows or the week's rollup. */
export interface WeeklyProjectTasks {
  project: Project;
  totalAmountClaimed: number;
  employeeTasks: WeeklyEmployeeTask[];
  miscEmployeeTasks: WeeklyMiscEmployeeTask[];
}

/** Aggregate every day's `projectEmployeeTasks` into one row per project, and one
 *  row per task / misc claim inside it. Days with no piece rows contribute nothing. */
export function aggregateWeeklyProjectTasks(days: Day[]): WeeklyProjectTasks[] {
  interface ProjectAcc {
    project: Project;
    totalAmountClaimed: number;
    employeeTasks: Map<number, WeeklyEmployeeTask>;
    miscEmployeeTasks: Map<string | number, WeeklyMiscEmployeeTask>;
  }
  const projectMap = new Map<string | number, ProjectAcc>();

  (days ?? []).forEach((day: Day) => {
    if (!day?.projectEmployeeTasks || day.projectEmployeeTasks.length === 0) return;

    day.projectEmployeeTasks.forEach((projectTask) => {
      const projectKey: string | number = projectTask.project.projectID || projectTask.project.altProjectID;

      if (!projectMap.has(projectKey)) {
        projectMap.set(projectKey, {
          project: { ...projectTask.project },
          totalAmountClaimed: 0,
          employeeTasks: new Map<number, WeeklyEmployeeTask>(),
          miscEmployeeTasks: new Map<string | number, WeeklyMiscEmployeeTask>(),
        });
      }

      const aggregatedProject = projectMap.get(projectKey)!;
      aggregatedProject.totalAmountClaimed += projectTask.totalAmountClaimed || 0;

      // Regular (piece) tasks — keyed by taskID.
      (projectTask.employeeTasks ?? []).forEach((task: EmployeeTask) => {
        const taskKey = task.task.taskID;
        if (!aggregatedProject.employeeTasks.has(taskKey)) {
          aggregatedProject.employeeTasks.set(taskKey, {
            task: { ...task.task },
            amountClaimed: 0,
            intermediateName: task.intermediateName,
          });
        }
        aggregatedProject.employeeTasks.get(taskKey)!.amountClaimed += task.amountClaimed || 0;
      });

      // Misc tasks — keyed by row ID, falling back to description + account code
      // for producers that omit the ID.
      (projectTask.miscEmployeeTasks ?? []).forEach((miscTask: MiscEmployeeTask) => {
        const miscKey: string | number =
          miscTask.miscEmployeeTaskID || `${miscTask.miscDescription}_${miscTask.accountCode?.accountCodeID}`;
        if (!aggregatedProject.miscEmployeeTasks.has(miscKey)) {
          aggregatedProject.miscEmployeeTasks.set(miscKey, {
            miscDescription: miscTask.miscDescription,
            accountCode: miscTask.accountCode,
            amountClaimed: 0,
            intermediateName: miscTask.intermediateName,
          });
        }
        aggregatedProject.miscEmployeeTasks.get(miscKey)!.amountClaimed += miscTask.amountClaimed || 0;
      });
    });
  });

  const result: WeeklyProjectTasks[] = [];
  projectMap.forEach((projectData) => {
    result.push({
      project: projectData.project,
      totalAmountClaimed: projectData.totalAmountClaimed,
      employeeTasks: Array.from(projectData.employeeTasks.values()),
      miscEmployeeTasks: Array.from(projectData.miscEmployeeTasks.values()),
    });
  });

  return result;
}

/** Total claimed across every project in the week's rollup. */
export function weeklyProjectTasksTotal(projects: WeeklyProjectTasks[]): number {
  return projects.reduce((s, p) => s + (p.totalAmountClaimed || 0), 0);
}
